<template>
	<Box title="Führerscheinklassen" :faulty="faulty">
		<FormGroup label="Besitzt du einen Führerschein?" :state="hasDrivingLicenseValid">
			<Select
				v-model="hasDrivingLicense"
				:options="hasDrivingLicenseOptions"
				undefined-text-option-text="--- Bitte wähle eine Führerscheinklasse aus ---"
				@input="hasDrivingLicenseChanged"
			/>
		</FormGroup>
		<div v-if="classesEligible" class="mt-3">
			<div
				v-for="(item, i) in classes"
				:key="item.id"
				class="inner-box mb-3"
			>
				<div class="top-row">
					<div class="title">Führerscheinklasse</div>
					<div class="actions">
						<a
							v-if="item.item || i > 0"
							href="#"
							v-b-tooltip
							title="Führerscheinklasse löschen"
							@click.prevent="classToRemove = item"
						>
							<Icon icon="bin" color="red"/>
						</a>
					</div>
				</div>
				<div>
					<FormGroup :state="item.item !== undefined">
						<SingleSelect
							v-model="item.item"
							:characteristics="meta.drivingLicenseClass"
							:exclude-ids="getIdsToExclude(item)"
							undefined-text-option-text="--- Bitte wähle eine Führerscheinklasse aus ---"
						/>
					</FormGroup>
				</div>
			</div>
		</div>
		<FormGroup
			v-if="moreEligible"
			label="Besitzt du noch eine weitere Führerscheinklasse?"
			:state="more === undefined"
			class="mt-3"
		>
			<MoreSelect
				v-model="more"
				:options="moreOptions"
				undefined-text-option-text="--- Bitte triff eine Auswahl ---"
				@input="moreChanged"
			/>
		</FormGroup>
		<ConfirmPopUp
			v-if="classToRemove"
			title="Führerscheinklasse löschen"
			@confirm="onRemove"
			@hidden="classToRemove = undefined"
		>
			Möchtest du die <b>Führerscheinklasse {{ classToRemove.item ? classToRemove.item.label : '' }}</b> wirklich
			löschen?
		</ConfirmPopUp>
	</Box>
</template>

<script>
import Box from "@/vue/common/Box";
import FormGroup from "@/vue/common/form/Group";
import Icon from "@/vue/icon/Icon"
import SingleSelect from "@/vue/characteristic/SingleSelect";
import MoreSelect from "@/vue/common/form/Select";
import Select from "@/vue/common/form/Select";
import ConfirmPopUp from "@/vue/common/popup/ConfirmPopUp";
import {isDefined} from "@/helper/Value";
import {uuid} from "vue-uuid";
import _ from "lodash";
import {characteristicSingle} from "@/helper/application/Populate";

export default {

	components: {
		FormGroup,
		ConfirmPopUp,
		Select,
		Icon,
		SingleSelect,
		MoreSelect,
		Box,
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		meta: {
			type: Object,
			required: true,
		},
		faulty: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	mounted ()
	{
		this.emitChangeEvent(false);
	},

	data ()
	{
		let hasDrivingLicense = undefined;
		let more = undefined;
		let classes = [];

		if (isDefined(this.value))
		{
			_.forEach(this.value.classes, classItem =>
			{
				classes.push(
					characteristicSingle(classItem, this.meta, this.meta.drivingLicenseClass, this.meta.drivingLicenseClassType)
				);
			});

			hasDrivingLicense = classes.length > 0;

			if (classes.length > 0)
			{
				more = false;
			}
		}

		if (classes.length === 0)
		{
			classes.push({
				id: uuid.v4(),
				item: undefined,
			});
		}

		return {
			classes,
			hasDrivingLicense,
			hasDrivingLicenseOptions: Object.freeze([
				{value: false, text: 'Nein, ich besitze keinen Führerschein'},
				{value: true, text: 'Ja, ich besitze einen Führerschein'},
			]),
			more,
			moreOptions: [
				{value: true, text: 'Ja, ich besitze noch eine weitere Führerscheinklasse'},
				{value: false, text: 'Nein, ich besitze keine weitere Führerscheinklasse'},
			],
			classToRemove: undefined,
		};
	},

	computed: {
		hasDrivingLicenseValid ()
		{
			return isDefined(this.hasDrivingLicense);
		},
		allClassesValid ()
		{
			return _.filter(this.classes, innerClass => isDefined(innerClass.item)).length === this.classes.length;
		},
		classesEligible ()
		{
			return this.hasDrivingLicense;
		},
		classesValid ()
		{
			if (!this.classesEligible)
			{
				return true;
			}

			return this.allClassesValid && this.more === false;
		},
		moreEligible ()
		{
			return this.classes.length > 0 && this.allClassesValid;
		},
		dataValid ()
		{
			return this.hasDrivingLicenseValid
				&& this.classesValid;
		},
	},

	methods: {
		hasDrivingLicenseChanged (hasDrivingLicense)
		{
			if (!hasDrivingLicense)
			{
				this.classes = [{
					id: uuid.v4(),
					item: undefined,
				}];

				this.more = undefined;
			}

			this.emitChangeEvent(true);
		},
		moreChanged (hasMore)
		{
			if (hasMore)
			{
				this.classes.push({
					id: uuid.v4(),
					item: undefined
				});

				this.more = undefined;
			}

			this.emitChangeEvent(true);
		},
		getIdsToExclude (item)
		{
			return _
				.chain(this.classes || [])
				.filter(innerItem => !item || innerItem !== item)
				.filter(innerItem => isDefined(innerItem.item))
				.map('item.id')
				.value();
		},
		onRemove ()
		{
			this.classes = _.filter(this.classes, innerClass => innerClass !== this.classToRemove);

			if (this.classes.length === 0)
			{
				this.classes.push({
					id: uuid.v4(),
					item: undefined,
				});

				this.more = undefined;
			}

			this.classToRemove = false;

			this.emitChangeEvent(true);
		},
		emitChangeEvent (fireChangeEvent)
		{
			if (!this.dataValid)
			{
				this.$emit('input', null);
				return;
			}

			const data = {
				classes: this.allClassesValid
					? this.classes
					: [],
			};

			this.$emit('input', data);

			if (fireChangeEvent)
			{
				this.$emit('change', data);
			}
		},
	},
}
</script>